import { useEffect, useMemo, useState } from 'react';
import { getServiceLogs } from '../api/client';

const SERVICES = [
  { id: 'roaster-modbus', label: 'Modbus' },
  { id: 'roaster-vision', label: 'Vision' },
];

function Logs() {
  const [selectedServices, setSelectedServices] = useState<string[]>(SERVICES.map((service) => service.id));
  const [lines, setLines] = useState(200);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [log, setLog] = useState('');
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const serviceLabel = useMemo(
    () => SERVICES.filter((service) => selectedServices.includes(service.id)).map((service) => service.label).join(' + '),
    [selectedServices]
  );

  async function load() {
    if (selectedServices.length === 0) {
      setLog('');
      setError('בחר לפחות שירות אחד');
      setLoading(false);
      return;
    }
    try {
      setError(null);
      const data = await getServiceLogs(selectedServices, lines);
      setLog(data.log || '');
      setUpdatedAt(new Date());
    } catch (err) {
      console.error(err);
      setError('לא ניתן לטעון לוגים מהשרת הנוכחי');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    load();
  }, [selectedServices, lines]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, selectedServices, lines]);

  function toggleService(serviceId: string) {
    setSelectedServices((current) =>
      current.includes(serviceId)
        ? current.filter((id) => id !== serviceId)
        : [...current, serviceId]
    );
  }

  return (
    <div className="space-y-5 min-h-0 flex flex-col" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-800">לוגים</h1>
          <p className="text-sm text-gray-500 mt-1">
            {serviceLabel || 'אין שירותים'} · {updatedAt ? updatedAt.toLocaleTimeString('he-IL') : 'טרם נטען'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {SERVICES.map((service) => (
            <button
              key={service.id}
              onClick={() => toggleService(service.id)}
              className={`px-4 py-2 rounded-xl text-sm font-bold border transition-colors ${
                selectedServices.includes(service.id)
                  ? 'bg-amber-800 text-white border-amber-900'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-amber-50'
              }`}
            >
              {service.label}
            </button>
          ))}
          <select
            value={lines}
            onChange={(event) => setLines(Number(event.target.value))}
            className="px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-800 font-bold"
          >
            <option value={100}>100 שורות</option>
            <option value={200}>200 שורות</option>
            <option value={500}>500 שורות</option>
            <option value={1000}>1000 שורות</option>
          </select>
          <label className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-gray-200 text-sm font-bold text-gray-700">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(event) => setAutoRefresh(event.target.checked)}
              className="accent-amber-700"
            />
            רענון אוטומטי
          </label>
          <button onClick={load} className="px-4 py-2 rounded-xl bg-gray-900 text-white font-bold">
            רענון
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 text-red-700 border border-red-100 p-4 font-bold">
          {error}
        </div>
      )}

      <section className="min-h-0 flex-1 rounded-2xl border border-gray-200 bg-gray-950 shadow-sm overflow-hidden" dir="ltr">
        {loading ? (
          <div className="h-full min-h-[360px] flex items-center justify-center text-gray-400">
            Loading logs...
          </div>
        ) : (
          <pre className="h-full min-h-[calc(100vh-230px)] max-h-[calc(100vh-230px)] overflow-auto whitespace-pre-wrap break-words p-4 text-sm leading-6 text-gray-100 font-mono">
            {log || 'No log lines returned.'}
          </pre>
        )}
      </section>
    </div>
  );
}

export default Logs;
